/* ============================================================================
 * 全局界面状态（主题 / 密度 / 对比度 / 导航 / 副驾）
 *
 * F-07  V2 的 [data-density="dense"] 已实现却从未暴露入口 → 此处接出
 * F-13  V2 的 [data-contrast="strict"] 合规开关同样从未暴露 → 此处接出
 * F-15  V2 副驾常驻吃掉 27% 宽度 → V3 默认收起（closed）
 * ========================================================================== */
import { create } from 'zustand';

export type Theme = 'dark' | 'light';
export type Density = 'default' | 'dense';
export type Contrast = 'default' | 'strict';
export type NavState = 'expanded' | 'collapsed';
export type AiState = 'open' | 'closed';

const LS = {
  theme: 'caps.theme',
  density: 'caps.density',
  contrast: 'caps.contrast',
  nav: 'caps.nav',
  ai: 'caps.ai',
  navGroups: 'caps.navgroups',
} as const;

function read<T extends string>(key: string, fallback: T): T {
  try { return (localStorage.getItem(key) as T) || fallback; } catch { return fallback; }
}
function write(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* 隐私模式忽略 */ }
}

/** 把状态同步到 <html> / <body> 属性上，CSS 靠这些属性选择器生效 */
function applyDom(s: Pick<UiState, 'theme' | 'density' | 'contrast' | 'nav' | 'ai'>) {
  const d = document.documentElement;
  d.setAttribute('data-theme', s.theme);
  if (s.density === 'dense') d.setAttribute('data-density', 'dense'); else d.removeAttribute('data-density');
  if (s.contrast === 'strict') d.setAttribute('data-contrast', 'strict'); else d.removeAttribute('data-contrast');
  document.body.setAttribute('data-nav', s.nav);
  document.body.setAttribute('data-ai', s.ai);
}

interface UiState {
  theme: Theme;
  density: Density;
  contrast: Contrast;
  nav: NavState;
  ai: AiState;
  openGroups: string[];

  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setDensity: (d: Density) => void;
  setContrast: (c: Contrast) => void;
  toggleNav: () => void;
  toggleAi: () => void;
  toggleGroup: (g: string) => void;
  ensureGroupOpen: (g: string) => void;
  syncDom: () => void;
}

export const useUi = create<UiState>((set, get) => ({
  theme: read<Theme>(LS.theme, 'dark'),
  density: read<Density>(LS.density, 'default'),
  contrast: read<Contrast>(LS.contrast, 'default'),
  nav: read<NavState>(LS.nav, 'expanded'),
  // F-15：副驾默认收起。V2 默认展开，@1280 时辅助栏占 46.3%，主内容仅 688px。
  ai: read<AiState>(LS.ai, 'closed'),
  openGroups: (() => {
    try {
      const raw = localStorage.getItem(LS.navGroups);
      if (raw) return JSON.parse(raw) as string[];
    } catch { /* noop */ }
    return ['planning', 'scheduling'];
  })(),

  setTheme: (t) => { write(LS.theme, t); set({ theme: t }); applyDom({ ...get(), theme: t }); },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
  setDensity: (d) => { write(LS.density, d); set({ density: d }); applyDom({ ...get(), density: d }); },
  setContrast: (c) => { write(LS.contrast, c); set({ contrast: c }); applyDom({ ...get(), contrast: c }); },
  toggleNav: () => {
    const nav: NavState = get().nav === 'expanded' ? 'collapsed' : 'expanded';
    write(LS.nav, nav); set({ nav }); applyDom({ ...get(), nav });
  },
  toggleAi: () => {
    const ai: AiState = get().ai === 'open' ? 'closed' : 'open';
    write(LS.ai, ai); set({ ai }); applyDom({ ...get(), ai });
  },
  toggleGroup: (g) => {
    const cur = get().openGroups;
    const next = cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g];
    write(LS.navGroups, JSON.stringify(next));
    set({ openGroups: next });
  },
  ensureGroupOpen: (g) => {
    const cur = get().openGroups;
    if (cur.includes(g)) return;
    const next = [...cur, g];
    write(LS.navGroups, JSON.stringify(next));
    set({ openGroups: next });
  },
  syncDom: () => applyDom(get()),
}));
